<!-- The applied-filter badges live in the shell (emd-active-filters), so every
     tab shows the same row rather than only this one. -->

<!-- KPI row + range selector -->
<div class="emd-kpi-row mb-3">
  <emd-kpi-card *ngFor="let card of kpiCards" [kpi]="card" class="emd-kpi-slot"></emd-kpi-card>
  <!-- Re-derives rather than just restyling: the range is the reporting window
       every figure on the tab is aggregated over. -->
  <emd-pill-group
    class="emd-kpi-range"
    [options]="ranges"
    [value]="activeRange"
    ariaLabel="Date range"
    (valueChange)="onRangeChange($event)"
  ></emd-pill-group>
</div>

<!-- Middle row: Wholesaler Sales (map + locations + value grid) | Net Sales -->
<div class="row g-3 mb-3">

  <!-- One widget: the map, its per-wholesaler location legend and the bubble
       size key are a single card, since the legend and size key are the map's
       own keys rather than standalone panels. -->
  <div class="col-lg-8">
    <div class="emd-card h-100 emd-map-card">

      <!-- US bubble map. It brings its own state boundaries, zoom controls and
           tooltip, so this card only supplies the header. -->
      <div class="emd-map-main">
        <emd-widget-header
          title="Wholesaler Sales"
          selectionOrigin="summary-map"
          infoText="Weekly sales quantity from the 867 feed, plotted by shipping city. Bubble size follows the Value Grid key and colours identify wholesalers. Click bubbles or legend rows to stage a selection, then confirm to cross-filter the dashboard."
        ></emd-widget-header>
        <div class="emd-map-host">
          <emd-bubble-map-chart
            [data]="mapBubbleData"
            [colorMap]="mapColors"
            [chartHeight]="500"
            (bubbleClick)="onBubbleClick($event)"
          ></emd-bubble-map-chart>
        </div>
      </div>

      <!-- Map keys: source colours, then the bubble radius scale. -->
      <div class="emd-map-keys">
        <div class="emd-card-header">
          <span class="emd-card-title">Wholesaler Locations</span>
        </div>
        <!-- Buttons, not divs: this is a cross-filter control, so it has to be
             reachable by keyboard as well as pointer. -->
        <div class="emd-legend-list">
          <button
            type="button"
            class="emd-legend-row"
            *ngFor="let ws of wholesalerLocations; trackBy: trackByName"
            [class.is-selected]="isWholesalerSelected(ws.name)"
            [class.is-dimmed]="isWholesalerDimmed(ws.name)"
            [attr.aria-pressed]="isWholesalerSelected(ws.name)"
            (click)="selectWholesaler(ws.name, 'summary-map')"
            (mouseenter)="highlightWholesaler(ws.name)"
            (mouseleave)="highlightWholesaler(null)"
            (focus)="highlightWholesaler(ws.name)"
            (blur)="highlightWholesaler(null)"
          >
            <span class="emd-legend-dot" [style.background]="colorFor(ws.name)"></span>
            <span class="emd-legend-name">{{ ws.name }}</span>
            <span class="emd-legend-count">{{ ws.locations }} locations</span>
          </button>
        </div>

        <hr class="emd-map-keys-divider" />

        <div class="emd-card-header">
          <span class="emd-card-title">Value Grid</span>
        </div>
        <div class="emd-value-grid">
          <div class="emd-vg-item" *ngFor="let vg of valueGrid">
            <svg [attr.width]="vg.r * 2" [attr.height]="valueGridMax * 2">
              <circle
                [attr.cx]="vg.r"
                [attr.cy]="valueGridMax"
                [attr.r]="vg.r - 1"
                fill="#e9edf3"
                stroke="#cbd5e1"
                stroke-width="1"
              ></circle>
            </svg>
            <span class="emd-vg-label">{{ vg.label }}</span>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Net Sales vs Returns by Wholesaler -->
  <div class="col-lg-4">
    <div class="emd-card h-100">
      <emd-widget-header
        title="Net Sales vs Returns by Wholesaler"
        subtitle="Net Sales QTY | Return QTY"
        selectionOrigin="summary-netsales"
        infoText="Net sales quantity against returned quantity per wholesaler for the selected range. The bar shows the net share of gross movement; the toggle switches the figures between units and dollars."
      >
        <emd-toggle [(value)]="netSalesToggle"></emd-toggle>
      </emd-widget-header>
      <div class="emd-netsales-list">
        <div
          class="emd-ns-row"
          *ngFor="let ns of netSalesData; trackBy: trackByName"
          role="button"
          tabindex="0"
          [class.is-selected]="isWholesalerSelected(ns.name)"
          [class.is-dimmed]="isWholesalerDimmed(ns.name)"
          [attr.aria-pressed]="isWholesalerSelected(ns.name)"
          (click)="selectWholesaler(ns.name, 'summary-netsales')"
          (keydown.enter)="selectWholesaler(ns.name, 'summary-netsales')"
          (keydown.space)="selectWholesaler(ns.name, 'summary-netsales')"
          (mouseenter)="highlightWholesaler(ns.name)"
          (mouseleave)="highlightWholesaler(null)"
        >
          <div class="emd-ns-info">
            <div class="emd-ns-name">{{ ns.name }}</div>
            <div class="emd-ns-sub">{{ ns.netPct }}% Net Sales</div>
          </div>
          <div class="emd-ns-track">
            <div class="emd-ns-fill emd-ns-fill--net" [style.width.%]="ns.netPct"></div>
            <div class="emd-ns-fill emd-ns-fill--ret" [style.width.%]="100 - ns.netPct"></div>
          </div>
          <div class="emd-ns-values">
            <div class="emd-ns-units">{{ formatNetSales(ns) }}</div>
            <div class="emd-ns-change" [class.positive]="ns.positive">{{ ns.change }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

</div><!-- /middle row -->

<!-- Bottom row: Sales Trend | % Sales by Wholesaler donut -->
<div class="row g-3">

  <div class="col-lg-7">
    <div class="emd-card h-100">
      <emd-widget-header
        title="Sales Trend"
        selectionOrigin="summary-trend"
        infoText="Total sales quantity (bars) against sales dollars (line) for the selected range. Drag the strip below the chart to zoom. Click bars to stage a period selection — confirming keeps only those periods everywhere."
      ></emd-widget-header>
      <!-- The chart draws its own clickable legend, so there is no hand-rolled
           swatch row here. -->
      <div class="emd-trend-host">
        <emd-bar-line-trend-chart
          [barData]="trendBarData"
          [barLabel]="'TOTAL SALES'"
          [barColor]="trendBarColor"
          [lineData]="trendLineData"
          [lineColors]="trendLineColors"
          [leftAxisLabel]="'Sales volume, $'"
          [rightAxisLabel]="'TOTAL SALES'"
          [dateFormat]="trendBucket"
          [chartHeight]="468"
          [selectedPeriods]="trendSelectedPeriods"
          (barClick)="onTrendBarClick($event)"
        ></emd-bar-line-trend-chart>
      </div>
    </div>
  </div>

  <div class="col-lg-5">
    <div class="emd-card h-100">
      <emd-widget-header
        title="% Sales by Wholesaler"
        selectionOrigin="summary-donut"
        infoText="Each wholesaler's share of sales for the selected range. The toggle switches the ring, centre total and legend between units and dollars; click segments to stage a selection."
      >
        <emd-toggle [value]="donutToggle" (valueChange)="onDonutToggleChange($event)"></emd-toggle>
      </emd-widget-header>

      <div class="emd-donut-wrap">
        <emd-donut-chart
          [data]="donutChartData"
          [size]="donutSize"
          [ringRatio]="0.62"
          [trackColor]="null"
          [segmentPadAngle]="0.012"
          [centerValue]="donutTotalLabel"
          [centerLabel]="'Total Sales'"
          [showTooltip]="true"
          (segmentClick)="onDonutSegmentClick($event)"
        ></emd-donut-chart>
      </div>

      <div class="emd-donut-legend">
        <button
          type="button"
          class="emd-dl-item"
          *ngFor="let d of donutData; trackBy: trackByName"
          [style.borderLeftColor]="colorFor(d.name)"
          [class.is-selected]="isWholesalerSelected(d.name)"
          [class.is-dimmed]="isWholesalerDimmed(d.name)"
          [attr.aria-pressed]="isWholesalerSelected(d.name)"
          (click)="selectWholesaler(d.name, 'summary-donut')"
          (mouseenter)="highlightWholesaler(d.name)"
          (mouseleave)="highlightWholesaler(null)"
        >
          <div class="emd-dl-name">{{ d.name }}</div>
          <div class="emd-dl-meta">{{ donutValueLabel(d) }} - {{ donutPct(d) }}%</div>
        </button>
      </div>
    </div>
  </div>

</div><!-- /bottom row -->
